import {
  AccountingReportType,
  ClientType,
  ENDPOINTS,
  generateOptions,
  Status,
  WebTable
} from '@constants';
import { Prototype } from '@core';
import {
  useExportOperationReportData,
  useMutateFetchOperationReportData
} from '@hooks/report/operation';
import DateInputWithCalender from '@pages/ReportV2/_components/DateInputWithCalender';
import {
  AccountingDateType,
  IReportAccountingFilter,
  JobContainerStatus
} from '@pages/ReportV2/helpers';
import { AgUtils } from '@utils';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { map } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import trans from 'translation';
import { KChip, KContainer, KForm, KGrid, KInput, KLabel } from 'uikit';

import Item from '../../_components/Item';

interface IFormData extends IReportAccountingFilter {}

const ReportInvoice = () => {
  const colDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: trans('invoice_number'),
        field: 'invoiceNumber',
        pinned: true,
        minWidth: 200
      },
      {
        headerName: trans('invoice_type'),
        field: 'invoiceType',
        pinned: true,
        cellClass: 'ag-cell-full-width',
        cellRenderer: ({ value = null, node }: ICellRendererParams) => {
          return (
            <KContainer.View
              dp="flex"
              row
              justifyContent={node.rowPinned ? 'flex-end' : 'flex-start'}
            >
              <KLabel.Text
                typo={node.rowPinned ? 'TextNmBold' : 'TextNmNormal'}
              >
                {value}
              </KLabel.Text>
            </KContainer.View>
          );
        }
      },
      {
        headerName: trans('invoice_status'),
        field: 'invoiceStatus'
      },
      {
        headerName: trans('invoice_date'),
        field: 'invoiceDate'
      },
      {
        headerName: trans('sync_status'),
        field: 'syncStatus'
      },
      {
        headerName: trans('sync_org_name'),
        field: 'syncOrgName'
      },
      {
        headerName: trans('sync_date'),
        field: 'syncDate'
      },
      {
        headerName: trans('sync_by'),
        field: 'syncBy'
      },
      {
        headerName: trans('reference_number'),
        field: 'referenceNumber'
      },
      {
        headerName: trans('total_amount'),
        field: 'totalAmount',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('total_due'),
        field: 'totalDue',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('due_date'),
        field: 'dueDate'
      },
      {
        headerName: trans('last_payment_date'),
        field: 'lastPaymentDate'
      },
      {
        headerName: trans('job_number'),
        field: 'jobNumber'
      },
      {
        headerName: trans('job_status'),
        field: 'jobStatus'
      },
      {
        headerName: trans('job_type'),
        field: 'jobType'
      },
      {
        headerName: trans('vessel'),
        field: 'vessel'
      },
      {
        headerName: trans('lloyd'),
        field: 'lloyd'
      },
      {
        headerName: trans('voyage'),
        field: 'voyage'
      },
      {
        headerName: trans('eta'),
        field: 'eta'
      },
      {
        headerName: trans('etd'),
        field: 'etd'
      },
      {
        headerName: trans('consignee_consignor'),
        field: 'consigneeConsignor'
      },
      {
        headerName: trans('account_receivable'),
        field: 'accountReceivable'
      },
      {
        headerName: trans('container_number'),
        field: 'containerNumbers'
      }
    ],
    []
  );

  const {
    data,
    mutate: mutateGenerate,
    isLoading
  } = useMutateFetchOperationReportData();

  const { mutate: mutateExport, isLoading: isExporting } =
    useExportOperationReportData();

  const methods = useForm<IFormData>({
    defaultValues: {
      isExport: false,
      ars: [],
      consigns: [],
      vessels: [],
      dateType: AccountingDateType.InvoiceDate,
      fromDate: '',
      toDate: '',
      jobStatus: JobContainerStatus.Active
    }
  });

  const [fromDate] = useWatch({
    control: methods.control,
    name: ['fromDate']
  });

  const onFormValid = useCallback(
    (_data: IFormData) => {
      const {
        isExport,
        ars,
        consigns,
        vessels,
        fromDate: _fromDate,
        toDate: _toDate,
        ...rest
      } = _data;

      const arIds = map(ars, 'id');
      const consignIds = map(consigns, 'id');
      const vesselIds = map(vessels, 'id');

      const _params = {
        reportExcelType: AccountingReportType.INVOICE.split('_')[0],
        arIds,
        consignIds,
        vesselIds,
        fromDate: Prototype.date.formatDB(_fromDate),
        toDate: Prototype.date.formatDB(_toDate),
        ...rest
      };

      const mutate = isExport ? mutateExport : mutateGenerate;

      mutate(_params);
    },
    [mutateExport, mutateGenerate]
  );

  const renderPinnedBottomRowDataFunc = useCallback((_data: any) => {
    return [
      {
        invoiceNumber: trans('total_invoices'),
        invoiceType: _data?.totalInvoices ?? 0
      },
      {
        invoiceNumber: trans('total_paid_invoices'),
        invoiceType: _data?.totalPaidInvoices ?? 0
      },
      {
        invoiceNumber: trans('total_unpaid_invoices'),
        invoiceType: _data?.totalUnpaidInvoices ?? 0
      },
      {
        invoiceNumber: trans('total_overdue_invoices'),
        invoiceType: _data?.totalOverdueInvoices ?? 0
      },
      {
        invoiceNumber: trans('total_amount'),
        invoiceType: Prototype.number.formatCurrency(_data?.totalAmount ?? 0)
      },
      {
        invoiceNumber: trans('total_paid_amount'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.totalPaidAmount ?? 0
        )
      },
      {
        invoiceNumber: trans('total_unpaid_amount'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.totalUnpaidAmount ?? 0
        )
      },
      {
        invoiceNumber: trans('total_overdue_amount'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.totalOverdueAmount ?? 0
        )
      },
      {
        invoiceNumber: trans('average_invoices_per_client'),
        invoiceType: _data?.averageInvoicesPerClient ?? 0
      },
      {
        invoiceNumber: trans('average_outstanding_balance_per_client'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.averageOutstandingBalancePerClient ?? 0
        )
      },
      {
        invoiceNumber: trans('average_amount_per_job'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.averageAmountPerJob ?? 0
        )
      },
      {
        invoiceNumber: trans('average_overdue_per_client'),
        invoiceType: Prototype.number.formatCurrency(
          _data?.averageOverduePerClient ?? 0
        )
      }
    ];
  }, []);

  return (
    <FormProvider {...methods}>
      <KForm onSubmit={methods.handleSubmit(onFormValid)}>
        <KGrid.Container>
          <KGrid.Item xs={4}>
            <Controller
              name="ars"
              control={methods.control}
              render={({ field }) => {
                return (
                  <KInput.Autocomplete
                    {...field}
                    label={trans('account_receivable')}
                    multiple
                    renderTags={(tagValue, getTagProps) => {
                      return tagValue?.map((i: any, index) => {
                        const isActive = i?.status === Status.Active;
                        return (
                          <KChip
                            key={i.id}
                            {...getTagProps({ index })}
                            label={i.name}
                            isLink
                            onPress={() => {
                              window.open(`/admin/clients/${i.id}`);
                            }}
                            color={isActive ? undefined : 'secondary'}
                          />
                        );
                      });
                    }}
                    apiURL={ENDPOINTS.client()}
                    apiParams={{
                      typeIn: [ClientType.AccountReceivable],
                      status: Status.Active
                    }}
                  />
                );
              }}
            />
          </KGrid.Item>

          <KGrid.Item xs={4}>
            <Controller
              name="consigns"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('consignee_consignor')}
                  multiple
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      const isActive = i?.status === Status.Active;
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.name}
                          isLink
                          onPress={() => {
                            window.open(`/admin/clients/${i.id}`);
                          }}
                          color={isActive ? undefined : 'secondary'}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.client()}
                  apiParams={{
                    typeIn: [ClientType.Consignee, ClientType.Consignor],
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={4}>
            <Controller
              name="vessels"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('vessel')}
                  multiple
                  getOptionLabel={(option: any) => option.vessel}
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.vessel}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.vessel()}
                  apiParams={{
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="dateType"
              control={methods.control}
              render={({ field }) => (
                <KInput.TextField
                  {...field}
                  label={trans('date_type')}
                  options={generateOptions(AccountingDateType)}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="fromDate"
              control={methods.control}
              render={({ field }) => (
                <DateInputWithCalender
                  {...field}
                  label={trans('from_date')}
                  // disabled={!dateType}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="toDate"
              control={methods.control}
              render={({ field }) => (
                <DateInputWithCalender
                  {...field}
                  label={trans('to_date')}
                  // disabled={!dateType || !fromDate}
                  minDate={Prototype.date.toMoment(fromDate) ?? undefined}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="jobStatus"
              control={methods.control}
              render={({ field }) => (
                <KInput.TextField
                  {...field}
                  label={trans('job_status')}
                  options={generateOptions(JobContainerStatus)}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={12}>
            <Item
              name={WebTable.ACCOUNTING_REPORT_STATEMENT}
              colDefs={colDefs}
              data={data}
              onSubmit={onFormValid}
              isGenerating={isLoading}
              isExporting={isExporting}
              renderPinnedBottomRowDataFunc={renderPinnedBottomRowDataFunc}
              height={1000}
            />
          </KGrid.Item>
        </KGrid.Container>
      </KForm>
    </FormProvider>
  );
};

export default memo(ReportInvoice);
